/* @jsx jsx */
import { css, jsx } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import { ColorName, Colors, TypographyName } from '../../../theme';
import { Text } from '../../display/Text';
import { TooltipWrapper } from '../../display/TooltipWrapper';

import PageNavigationRow from './PageNavigationRow';
import type { ContentProps, OnSelectType, PageNavigationItem } from './types';
import checkIsTruncated from './utils/checkIsTruncated';

const Styles = {
  textEllipsis: (textColor: ColorName) => css`
    color: ${Colors[textColor]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  // The width value for textWrap is 90% of the row width, less the 33px
  // difference in text indentation between the first and second nested
  // rows.
  textWrap: css`
    align-items: center;
    border-left: 1px solid ${Colors.GrayWarm4};
    display: flex;
    flex-direction: row;
    padding: var(--zds-space-8) 0px;
    width: calc(90% - 33px);
  `,
  rowContainer: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 0px var(--zds-space-24) 0px 60px;
    position: relative;
  `,
};

export default function NestedThirdRow({
  item,
  onSelect,
}: {
  item: PageNavigationItem;
  onSelect?: OnSelectType;
}) {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setIsTruncated(checkIsTruncated(textRef));
  }, []);

  const InnerContent = ({ hover, selected }: ContentProps) => {
    const textColor: ColorName =
      hover || selected ? 'UiPrimaryStrongest' : 'GrayWarm10';
    const textType: TypographyName = selected ? 'Body3' : 'Body2';

    return (
      <div css={Styles.rowContainer} data-zds>
        <span css={Styles.textWrap} data-zds>
          <span css={Styles.textEllipsis(textColor)} ref={textRef} data-zds>
            <Text color={textColor} margin="0 0 0 8px" type={textType}>
              {item.title}
              {item.count && ` (${item.count})`}
            </Text>
          </span>
        </span>
      </div>
    );
  };

  const Content = ({ hover, selected }: ContentProps) =>
    isTruncated ? (
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={item.count ? `${item.title} (${item.count})` : item.title}
        isBlock={true}
        position="northeast"
      >
        {() => <InnerContent hover={hover} selected={selected} />}
      </TooltipWrapper>
    ) : (
      <InnerContent hover={hover} selected={selected} />
    );

  return (
    <PageNavigationRow content={Content} item={item} onSelect={onSelect} />
  );
}
